import React, { useState, useEffect } from 'react';
import { getHistory } from '../services/historyManager';
import type { WebsiteData, HistorySnapshot } from '../types';

interface WebsiteHistoryProps {
  onSelectWebsite: (data: WebsiteData) => void;
}

export const WebsiteHistory: React.FC<WebsiteHistoryProps> = ({ onSelectWebsite }) => {
  const [history, setHistory] = useState<HistorySnapshot[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const loadHistory = () => {
      const historyData = getHistory();
      // Reverse to show most recent first
      setHistory(historyData.reverse());
    };

    loadHistory();
    // Refresh history every 5 seconds if user is on this page
    const interval = setInterval(loadHistory, 5000);
    return () => clearInterval(interval);
  }, []);

  if (history.length === 0) {
    return null; // Don't show section if no history
  }

  return (
    <div className="mt-6 md:mt-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
        <div>
          <h3 className="text-base md:text-lg font-bold text-gray-800 dark:text-gray-200">Recent Websites</h3>
          <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Continue working on your projects</p>
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-500">
          {history.length} {history.length === 1 ? 'project' : 'projects'}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {history.slice(0, showAll ? history.length : 6).map((snapshot) => {
          const data = snapshot.data;
          const websiteTitle = data.navbar?.brandName || data.hero?.headline || 'Untitled Website';
          const aiModel = data.preferredModel || 'gemini';
          const date = new Date(snapshot.timestamp);

          return (
            <div
              key={snapshot.timestamp}
              className={`group bg-white dark:bg-gray-800 rounded-xl border-2 transition-all cursor-pointer overflow-hidden hover:shadow-lg ${
                selectedId === snapshot.timestamp
                  ? 'border-purple-500 ring-2 ring-purple-200 dark:ring-purple-900/50'
                  : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700'
              }`}
              onClick={() => {
                setSelectedId(snapshot.timestamp);
                onSelectWebsite(data);
              }}
            >
              {/* Preview Image */}
              <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center relative overflow-hidden">
                {/* Generate a simple preview based on website data */}
                <div className="absolute inset-0 p-4 text-xs opacity-60 overflow-hidden">
                  <div className="font-bold mb-2 text-gray-700 dark:text-gray-300">
                    {data.navbar?.brandName || 'Website'}
                  </div>
                  {data.hero && (
                    <div className="mb-2 text-gray-600 dark:text-gray-400">
                      <div className="font-semibold">{data.hero.headline}</div>
                      <div className="text-xs">{data.hero.subheadline?.slice(0, 40)}...</div>
                    </div>
                  )}
                  {data.services?.services.slice(0, 2).map((service, idx) => (
                    <div key={idx} className="mb-1 text-gray-500 dark:text-gray-500">
                      • {service.title}
                    </div>
                  ))}
                </div>

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
                  <button className="bg-white/90 dark:bg-gray-900/90 text-gray-800 dark:text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-white dark:hover:bg-gray-900 transition-colors">
                    Open Project
                  </button>
                </div>
              </div>

              {/* Info */}
              <div className="p-4">
                <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2 truncate">
                  {websiteTitle}
                </h4>
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2 py-1 rounded-full font-medium ${
                        aiModel === 'gemini'
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                          : 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                      }`}
                    >
                      {aiModel === 'gemini' ? '✨ Gemini' : '🧠 Claude'}
                    </span>
                  </div>
                  <span className="text-gray-500 dark:text-gray-500">
                    {date.toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {history.length > 6 && (
        <div className="mt-4 text-center">
          <button 
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-purple-600 dark:text-purple-400 hover:underline font-medium transition-colors"
          >
            {showAll ? '← Show less' : `View all ${history.length} projects →`}
          </button>
        </div>
      )}
    </div>
  );
};
